import { useState, FormEvent } from 'react';
import kit from './kit.module.css';
import styles from './ActionLogDemo.module.css';

// ── Live log scenario ─────────────────────────────────────────────────────────

type EntryStatus = 'completed' | 'flagged';
type FlagState = 'idle' | 'open' | 'submitted';

interface LogEntry {
  id: number;
  time: string;
  description: string;
  status: EntryStatus;
  /** Only set for actions that can be reversed. Sent messages and failed actions have none. */
  undo?: { label: string; result: string };
}

const ENTRIES: LogEntry[] = [
  { id: 1, time: '2m ago', description: 'Sent CV summary for James Okafor to the employer contact on file. No response yet.', status: 'completed' },
  {
    id: 2,
    time: '1h ago',
    description: "Detected missing employment dates in Sarah Mitchell's profile. Added a note for key worker review.",
    status: 'completed',
    undo: { label: 'Undo', result: "Reversed by you just now - the note was removed from Sarah Mitchell's profile." },
  },
  { id: 3, time: '3h ago', description: 'Reminded key worker: 2 CVs awaiting review before end of day.', status: 'completed' },
  { id: 4, time: '5h ago', description: 'Tried to retrieve the employer contact for case #4821. Contact not found - flagged for manual follow-up.', status: 'flagged' },
  { id: 5, time: '1d ago', description: 'Generated CV for Tom Brennan. Key worker approved and downloaded it.', status: 'completed' },
];

function FeedbackForm({ onSubmit }: { onSubmit: () => void }) {
  const [value, setValue] = useState('');
  function handleSubmit(e: FormEvent) {
    e.preventDefault();
    onSubmit();
  }
  return (
    <form className={styles.form} onSubmit={handleSubmit}>
      <label className={kit.label}>
        What was incorrect?
        <input
          className={styles.input}
          type="text"
          placeholder="Describe the issue..."
          value={value}
          onChange={(e) => setValue(e.target.value)}
          autoFocus
        />
      </label>
      <div className={kit.row}>
        <button type="submit" className={`${kit.btn} ${kit.btnEdit}`} disabled={!value.trim()}>
          Submit feedback
        </button>
      </div>
    </form>
  );
}

function LogEntryRow({ entry }: { entry: LogEntry }) {
  const [flagState, setFlagState] = useState<FlagState>('idle');
  const [reversed, setReversed] = useState(false);
  const flagged = entry.status === 'flagged';
  const chipLabel = reversed ? 'Reversed' : flagged ? 'Flagged' : flagState === 'submitted' ? 'Reported' : 'Done';
  const chipClass = reversed ? '' : flagged || flagState === 'submitted' ? kit.chipWarn : '';
  return (
    // A reversed entry stays in the log - entries are never deleted
    <div className={`${kit.logRow} ${reversed ? styles.reversed : ''}`}>
      <span className={kit.logTime}>{entry.time}</span>
      <span className={`${kit.logDot} ${reversed ? '' : flagged ? styles.dotWarn : kit.logDotDone}`} />
      <div className={styles.entry}>
        <p className={styles.description}>{entry.description}</p>
        {reversed ? (
          <p className={kit.feedback}>{entry.undo?.result}</p>
        ) : (
          <>
            {flagState === 'idle' && (
              <div className={styles.entryActions}>
                {entry.undo && (
                  <button className={styles.link} onClick={() => setReversed(true)}>
                    {entry.undo.label}
                  </button>
                )}
                <button className={styles.link} onClick={() => setFlagState('open')}>
                  Flag as incorrect
                </button>
              </div>
            )}
            {flagState === 'open' && <FeedbackForm onSubmit={() => setFlagState('submitted')} />}
            {flagState === 'submitted' && <p className={kit.feedback}>Thanks - this helps improve the agent.</p>}
          </>
        )}
      </div>
      <span className={`${kit.chip} ${styles.status} ${chipClass}`}>{chipLabel}</span>
    </div>
  );
}

function LiveLogScenario() {
  return (
    <div className={kit.proposal}>
      <div className={kit.proposalTop}>
        <span>Agent activity · last 24 hours</span>
        <span className={styles.live}>
          <span className={`${kit.logDot} ${kit.logDotActive}`} aria-hidden="true" />
          Live
        </span>
      </div>
      <div className={`${kit.log} ${styles.log}`}>
        {ENTRIES.map((entry) => (
          <LogEntryRow key={entry.id} entry={entry} />
        ))}
      </div>
    </div>
  );
}

// ── Bulk actions scenario ─────────────────────────────────────────────────────

type BulkStatus = 'completed' | 'review' | 'failed';

interface BulkEntry {
  id: number;
  description: string;
  status: BulkStatus;
  time: string;
  details: string[];
  /** Only set for actions that can be reversed */
  undo?: { label: string; result: string };
}

const BULK_ENTRIES: BulkEntry[] = [
  {
    id: 1,
    description: 'Generated CVs for 7 jobseekers',
    status: 'completed',
    time: '11:32',
    details: [
      'James Okafor - CV generated and saved',
      'Sarah Mitchell - CV generated and saved',
      'Tom Brennan - CV generated and saved',
      'Aisha Patel - CV generated and saved',
      'Marcus Webb - CV generated and saved',
      'Lena Kovacs - CV generated and saved',
      'Remi Osei - CV generated and saved',
    ],
  },
  {
    id: 2,
    description: 'Flagged 2 profiles: missing employment history',
    status: 'review',
    time: '11:34',
    details: ['Case #3812 - No employment dates provided', 'Case #4107 - Employment history section blank'],
    undo: { label: 'Undo', result: 'Reversed by you just now - both profiles were unflagged.' },
  },
  {
    id: 3,
    description: 'Tried to send CV for case #4821 - employer email not found',
    status: 'failed',
    time: '11:35',
    details: ['Case #4821 - Looked up employer contact: not found', 'Flagged for manual follow-up by key worker'],
  },
  {
    id: 4,
    description: 'Sent daily summary report to the team',
    status: 'completed',
    time: '11:36',
    details: ['7 CVs generated successfully', '2 cases flagged for review', '1 action failed - manual follow-up required'],
  },
];

const STATUS: Record<BulkStatus, { label: string; chip: string; dot: string }> = {
  completed: { label: 'Done', chip: '', dot: kit.logDotDone },
  review: { label: 'Review', chip: kit.chipWarn, dot: styles.dotWarn },
  failed: { label: 'Failed', chip: kit.chipBad, dot: kit.logDotError },
};

function BulkEntryRow({ entry }: { entry: BulkEntry }) {
  const [expanded, setExpanded] = useState(false);
  const [reversed, setReversed] = useState(false);
  const s = STATUS[entry.status];
  return (
    // A reversed entry stays in the log - entries are never deleted
    <div className={`${kit.logRow} ${reversed ? styles.reversed : ''}`}>
      <span className={kit.logTime}>{entry.time}</span>
      <span className={`${kit.logDot} ${reversed ? '' : s.dot}`} />
      <div className={styles.entry}>
        <p className={styles.description}>{entry.description}</p>
        {reversed && <p className={kit.feedback}>{entry.undo?.result}</p>}
        <div className={styles.entryActions}>
          {entry.undo && !reversed && (
            <button className={styles.link} onClick={() => setReversed(true)}>
              {entry.undo.label}
            </button>
          )}
          <button className={styles.link} onClick={() => setExpanded((v) => !v)} aria-expanded={expanded}>
            {expanded ? 'Hide details' : 'See details'} <span aria-hidden="true">{expanded ? '▴' : '▾'}</span>
          </button>
        </div>
        {expanded && (
          <ul className={styles.details}>
            {entry.details.map((d) => (
              <li key={d}>{d}</li>
            ))}
          </ul>
        )}
      </div>
      <span className={`${kit.chip} ${styles.status} ${reversed ? '' : s.chip}`}>{reversed ? 'Reversed' : s.label}</span>
    </div>
  );
}

function BulkActionsScenario() {
  return (
    <>
      <div className={kit.agent}>
        <span className={kit.agentIcon} aria-hidden="true">✦</span>
        <p>While you were away, I processed 10 cases. Here's the summary.</p>
      </div>
      <div className={kit.row}>
        <span className={`${kit.chip} ${kit.chipGood}`}>7 CVs generated</span>
        <span className={`${kit.chip} ${kit.chipWarn}`}>2 need review</span>
        <span className={`${kit.chip} ${kit.chipBad}`}>1 failed</span>
      </div>
      <div className={kit.proposal}>
        <div className={kit.proposalTop}>
          <span>Last night's activity</span>
          <span>11:32 - 11:36 PM</span>
        </div>
        <div className={`${kit.log} ${styles.log}`}>
          {BULK_ENTRIES.map((entry) => (
            <BulkEntryRow key={entry.id} entry={entry} />
          ))}
        </div>
      </div>
    </>
  );
}

// ── Top-level component with scenario tabs ────────────────────────────────────

type Tab = 'live' | 'bulk';

const TABS: { id: Tab; label: string }[] = [
  { id: 'live', label: 'Live log' },
  { id: 'bulk', label: 'Bulk actions' },
];

export function ActionLogDemo() {
  const [tab, setTab] = useState<Tab>('live');

  return (
    <div className={kit.root}>
      <div className={kit.row} role="tablist" aria-label="Scenario">
        {TABS.map((t) => (
          <button
            key={t.id}
            role="tab"
            aria-selected={tab === t.id}
            className={`${kit.chip} ${styles.tab} ${tab === t.id ? styles.tabActive : ''}`}
            onClick={() => setTab(t.id)}
          >
            {t.label}
          </button>
        ))}
      </div>
      <div key={tab} className={kit.stack}>
        {tab === 'live' ? <LiveLogScenario /> : <BulkActionsScenario />}
      </div>
    </div>
  );
}
